import { SocketMessage } from "../../../../../hooks/useSocket";
import { Button } from "../../../../../components/ui/button";
import { cn } from "../../../../../lib/utils";
import { Check, CheckCheck, CloudCog, Reply, Smile, X } from "lucide-react";
import { useEffect, useRef, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type Message = SocketMessage;

interface Reaction {
  emoji: string;
  label: string;
}

const reactions: Reaction[] = [
  { emoji: "👍", label: "Like" },
  { emoji: "❤️", label: "Love" },
  { emoji: "😂", label: "Laugh" },
  { emoji: "😮", label: "Wow" },
  { emoji: "😢", label: "Sad" },
];

const MessageBubble = memo(
  ({
    message,
    isOwn,
    onReply,
    onReact,
    index,
    processId,
    onPaneReferenceClick,
  }: {
    message: Message;
    isOwn: boolean;
    onReply: (message: Message) => void;
    onReact: (messageId: string, reaction: string) => void;
    index: number;
    processId?: string;
    onPaneReferenceClick?: (reference: {
      screen: string;
      screenLabel: string;
      component?: string;
      componentKey?: string;
    }) => void;
  }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "he";
    const [showReactions, setShowReactions] = useState(false);
    const reactionBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          reactionBoxRef.current &&
          !reactionBoxRef.current.contains(event.target as Node)
        ) {
          setShowReactions(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div
        className={cn(
          "flex group",
          isOwn ? "justify-end" : "justify-start" // LTR: own messages on right, others on left
        )}
      >
        <div
          className={cn(
            "max-w-[70%] relative",
            isRTL
              ? isOwn
                ? "order-1"
                : "order-2" // RTL: own messages first, others second
              : isOwn
              ? "order-2"
              : "order-1" // LTR: own messages second, others first
          )}
        >
          <div
            className={cn(
              "px-3 py-2 rounded-lg relative group/message",
              isOwn
                ? isRTL
                  ? "bg-primary/30 text-gray-900 rounded-bl-none" // RTL: own messages have left border radius removed
                  : "bg-primary/30 text-gray-900 rounded-br-none" // LTR: own messages have right border radius removed
                : isRTL
                ? "bg-white text-gray-900 rounded-br-none shadow-lg" // RTL: other messages have right border radius removed
                : "bg-white text-gray-900 rounded-bl-none shadow-lg" // LTR: other messages have left border radius removed
            )}
            onDoubleClick={() => setShowReactions(!showReactions)}
          >
            {/* WhatsApp-style quoted message */}
            {message.replyTo && (
              <div
                className={cn(
                  "mb-2 p-2 rounded-md relative overflow-hidden cursor-pointer",
                  "transition-colors duration-150",
                  isOwn
                    ? "bg-white/60 hover:bg-white/80"
                    : "bg-gray-50 hover:bg-gray-100"
                )}
              >
                {/* Vertical accent line - Primary color */}
                <div
                  className={cn(
                    "absolute left-0 top-0 bottom-0 w-1 rounded-r",
                    isOwn ? "bg-[#5D8BED]" : "bg-[#5D8BED]"
                  )}
                />

                {/* Quote content */}
                <div className="pl-3">
                  {/* Sender name */}
                  <div
                    className={cn(
                      "text-sm font-medium mb-1",
                      isOwn ? "text-[#5D8BED]" : "text-[#5D8BED]",
                      isRTL ? "text-right" : "text-left"
                    )}
                  >
                    {message.replyTo.senderName}
                  </div>

                  {/* Quoted text */}
                  <div
                    className={`text-sm text-gray-600 leading-relaxed ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    {message.replyTo.text.length > 100
                      ? `${message.replyTo.text.substring(0, 100)}...`
                      : message.replyTo.text}
                  </div>
                </div>
              </div>
            )}

            {/* Pane Reference */}
            {message.referenceType === "screen" &&
              message.reference?.screenLabel && (
                <div
                  className={cn(
                    "mb-2 p-2 bg-primary/5 border border-primary/20 rounded-lg",
                    onPaneReferenceClick &&
                      "cursor-pointer hover:bg-primary/10 hover:border-primary/30 transition-colors"
                  )}
                  onClick={() => {
                    if (onPaneReferenceClick && message.reference) {
                      // Debug log to see what we're getting
                      console.log("[MessageBubble] Pane reference data:", message.reference);

                      onPaneReferenceClick({
                        screen: message.reference.screenId || "",
                        screenLabel: message.reference.screenLabel || "",
                        component: message.reference.componentLabel,
                        componentKey: message.reference.componentId,
                      });
                    }
                  }}
                  title={
                    onPaneReferenceClick
                      ? t("click_to_navigate") || "Click to navigate to this step"
                      : undefined
                  }
                >
                  <div className="flex items-center gap-2">
                    <CloudCog className="h-4 w-4 text-primary" />
                    <span className="text-xs font-medium text-primary">
                      {t("reference")}:{" "}
                      {message.reference.screenLabel ||
                        message.reference.screenId}
                      {message.reference.componentLabel && (
                        <span className="text-primary ml-1">
                          → {message.reference.componentLabel}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}

            {/* Main message content */}
            <div
              className={cn(
                "text-sm leading-relaxed break-words",
                "prose prose-sm max-w-none",
                "prose-headings:font-semibold prose-headings:text-gray-900",
                "prose-p:my-1 prose-p:leading-relaxed",
                "prose-strong:font-bold prose-strong:text-gray-900",
                "prose-em:italic prose-em:text-gray-800",
                "prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:text-gray-800 prose-code:before:content-[''] prose-code:after:content-['']",
                "prose-pre:bg-gray-100 prose-pre:border prose-pre:border-gray-200",
                "prose-ul:my-2 prose-ol:my-2",
                "prose-li:my-0.5",
                "prose-a:text-primary prose-a:underline",
                isRTL ? "text-right" : "text-left"
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.text}
              </ReactMarkdown>
            </div>

            {/* Message info */}
            <div
              className={cn(
                "flex items-center gap-1 mt-1 justify-between"
                // isOwn
                //   ? isRTL
                //     ? "justify-end"
                //     : "justify-start" // RTL: own messages align info to left
                //   : isRTL
                //   ? "justify-end"
                //   : "justify-start" // RTL: other messages align info to right
              )}
            >
              <span className="text-xs text-gray-500">
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {isOwn && (
                <div className="text-xs text-gray-500 ml-1">
                  {message.isMessageRead ? (
                    <CheckCheck className="w-4 h-4 inline text-primary" />
                  ) : (
                    <Check className="w-4 h-4 inline text-gray-400" />
                  )}
                </div>
              )}
            </div>

            {/* Message reactions - WhatsApp style */}
            {message.reactions.length > 0 && (
              <div
                className={cn(
                  "absolute -bottom-3.5 flex gap-1",
                  isOwn
                    ? isRTL
                      ? "left-2"
                      : "right-2" // RTL: own messages show reactions on left
                    : isRTL
                    ? "right-2"
                    : "left-2" // RTL: other messages show reactions on right
                )}
              >
                {message.reactions.map((reaction, index) => (
                  <span
                    key={index}
                    className="w-6 h-6 flex items-center justify-center bg-white border border-gray-200 rounded-full text-sm cursor-pointer select-none hover:bg-gray-50 transition-colors shadow-sm leading-none"
                    onClick={() => setShowReactions(!showReactions)}
                  >
                    {reaction.reaction}
                  </span>
                ))}
              </div>
            )}

            {/* Hover actions - WhatsApp style */}
            <div
              className={cn(
                "absolute top-2 opacity-0 group-hover/message:opacity-100 transition-opacity duration-200 flex gap-1",
                isOwn
                  ? "-left-[4.6rem]" // RTL: own messages show actions on right
                  : "-right-[4.6rem]" // RTL: other messages show actions on left
              )}
            >
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 bg-background/80 text-foreground shadow-sm"
                onClick={() => onReply(message)}
              >
                <Reply className="w-4 h-4 text-gray-600" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 bg-background/80 text-foreground shadow-sm"
                onClick={() => setShowReactions(!showReactions)}
              >
                <Smile className="w-4 h-4 text-gray-600" />
              </Button>
            </div>
          </div>

          {/* Reaction picker - WhatsApp style */}
          {showReactions && (
            <div
              ref={reactionBoxRef}
              className={`absolute mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1 z-10 select-none ${
                isOwn
                  ? "right-0" // RTL: own messages show reactions on left
                  : "left-0" // RTL: other messages show reactions on right
              } ${index === 0 ? "bottom-14" : "top-full"}`}
            >
              {reactions.map((reaction) => (
                <Button
                  key={reaction.emoji}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-100 transition-colors rounded"
                  onClick={() => {
                    onReact(message._id, reaction.emoji);
                    setShowReactions(false);
                  }}
                >
                  {reaction.emoji}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default MessageBubble;
